export const USERS_FRESH_MS = 60_000;
export const USERS_MAX_AGE_MS = 24 * 60 * 60_000;
export interface UsersSnapshot<T> { owner: string; savedAt: number; data: T }
export interface SnapshotStorage<T> {
    read(): Promise<UsersSnapshot<T> | null>;
    write(snapshot: UsersSnapshot<T>, isCurrent: () => boolean): Promise<void>;
    clear(): Promise<void>;
}

export class SupersededUsersRequest extends Error {}

// One owner per browser snapshot. No bearer tokens or public/CDN cache.
export class UsersSnapshotCache<T> {
    private snapshot: UsersSnapshot<T> | null = null;
    private epoch = 0;
    private pending: { owner: string; promise: Promise<UsersSnapshot<T>> } | null = null;
    constructor(private storage: SnapshotStorage<T>, private now = Date.now) {}

    peek(owner: string) {
        const entry = this.snapshot;
        const age = entry ? this.now() - entry.savedAt : Infinity;
        return entry?.owner === owner && age >= 0 && age <= USERS_MAX_AGE_MS ? entry : null;
    }

    async restore(owner: string) {
        const memory = this.peek(owner);
        if (memory) return memory;
        const epoch = this.epoch;
        const disk = await this.storage.read().catch(() => null);
        if (epoch !== this.epoch) return null;
        if (disk?.owner !== owner || this.now() - disk.savedAt > USERS_MAX_AGE_MS) return null;
        // A slower disk read must not replace a completed network refresh.
        if (!this.snapshot || disk.savedAt > this.snapshot.savedAt) this.snapshot = disk;
        return this.peek(owner);
    }

    async refresh(owner: string, loader: () => Promise<T>, force = false) {
        const cached = this.peek(owner);
        if (!force && cached && this.now() - cached.savedAt < USERS_FRESH_MS) return cached;
        if (!force && this.pending?.owner === owner) return this.pending.promise;
        const epoch = ++this.epoch;
        const promise = (async () => {
            const data = await loader();
            if (epoch !== this.epoch) throw new SupersededUsersRequest();
            const snapshot = { owner, data, savedAt: this.now() };
            this.snapshot = snapshot;
            // Disk persistence must never hold up the visible table.
            void this.storage.write(snapshot, () => epoch === this.epoch).catch(() => {});
            return snapshot;
        })();
        this.pending = { owner, promise };
        try { return await promise; }
        finally { if (epoch === this.epoch) this.pending = null; }
    }

    clear() {
        ++this.epoch;
        this.snapshot = null;
        this.pending = null;
        void this.storage.clear().catch(() => {});
    }
}

const DB_NAME = 'checkcoa-admin-users-v1';
const STORE = 'snapshot';
async function openCache(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => request.result.createObjectStore(STORE);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('User cache is unavailable'));
    });
}

const diskStorage: SnapshotStorage<unknown> = {
    async read() {
        const db = await openCache();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const request = tx.objectStore(STORE).get('current');
            request.onsuccess = () => {
                const entry = request.result;
                resolve(entry?.owner && Number.isFinite(entry.savedAt)
                    && ['users', 'riskRows', 'loginRows', 'stripeSubscriptions', 'communityRows']
                        .every(key => Array.isArray(entry.data?.[key])) ? entry : null);
            };
            request.onerror = () => reject(request.error);
            tx.oncomplete = () => db.close();
            tx.onabort = () => { db.close(); reject(tx.error); };
        });
    },
    async write(entry, isCurrent) {
        const db = await openCache();
        if (!isCurrent()) { db.close(); return; }
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).put(entry, 'current');
            tx.oncomplete = () => { db.close(); resolve(); };
            tx.onabort = () => { db.close(); reject(tx.error); };
            tx.onerror = () => { db.close(); reject(tx.error); };
        });
    },
    async clear() {
        const db = await openCache();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).clear();
            tx.oncomplete = () => { db.close(); resolve(); };
            tx.onabort = () => { db.close(); reject(tx.error); };
        });
    },
};

export const adminUsersCache = new UsersSnapshotCache(diskStorage);
